import EditRounded from "@mui/icons-material/EditRounded";
import { CardContent, Grid, MenuItem, Theme, Typography, useTheme, styled } from "@mui/material";
import { IFlightPairingStatus, IFPSActualCrew, IFullFlight, IAircraft } from "@vmosolutions/oc-interfaces";
import { useEffect, useMemo, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import ContextMenu from "src/components/ContextMenu/ContextMenu";
import { PositionBadge } from "src/components/CrewSchedule/PositionBadge";
import { EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { FlightChip, IFlightChip } from "src/components/Flights/FlightChip";
import { IconButton } from "src/components/IconButton/IconButton";
import { ShortCodeChip } from "src/components/ShortCodes/ShortCodeChip";
import { closeContextMenu } from "src/redux/actions/ContextMenu.Actions";
import { setSelectedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { v1 } from "uuid";

interface IFlightPairingStatusContextProps {
  id: string;
  path: string[];
  setActiveEventModal: (activeEventModal: EventModalTypes) => void;
}

const crewStatus = (theme: Theme): object => ({
  height: 20,
  textAlign: "center",
  margin: 5,
  color: theme.palette.background.default,
  fontSize: 10,
  border: `1px solid ${theme.palette.background.default}`,
  borderRadius: 3,
  padding: "2px 8px",
  fontWeight: 700,
  textShadow: "1px 1px rgba(255,255,255,0.5)", // alphad drop shadow on text to improve legibility.
  // Flight Pairing Status Colors
  "&.no_pairing": { backgroundColor: theme.custom.pairingStatus.noPairingColor },
  "&.missing_crew": { backgroundColor: theme.custom.pairingStatus.missingCrewColor },
  "&.extra_crew": { backgroundColor: theme.custom.pairingStatus.extraCrewColor },
  "&.extra_pos": { backgroundColor: theme.custom.pairingStatus.extraPosColor },
  "&.missing_ac_info": { backgroundColor: theme.custom.pairingStatus.missingACInfoColor },
  "&.ok": { backgroundColor: theme.custom.pairingStatus.okColor },
});

const OCContextMenu = styled(ContextMenu)(({ theme }) => ({
  "& .MuiCard-root": { height: "100%" },

  "& .edit_flight_btn, .edit_pairing_btn": {
    width: 20,
    height: 20,
    padding: 0,
    margin: 5,
    borderRadius: 3,
    border: "1px solid",
    "& .MuiSvgIcon-root": {
      width: 14,
      height: 14,
      borderRadius: 3,
      margin: "0px 5px",
    },
  },

  "& .flight_info": {
    "& .flightchip": { marginBottom: 10 },
    "& .info": { margin: "2px 5px", fontSize: 12 },
    "& .flight_status_container": {
      display: "flex",
      flexDirection: "row",
      "& .status": crewStatus(theme),
    },
  },

  "& .pairing_info": {
    padding: "10px 16px",
    margin: "0px 0px 10px 0px",
    backgroundColor: theme.custom.timeline.contextListBackground,
    "& .info": { margin: "2px 5px", fontSize: 12 },
    "& .pairing_positions": {
      display: "flex",
      flexDirection: "row",
      "& .crew_badge": { justifyContent: "center" },
    },
    "& .pairing_status_container": {
      display: "flex",
      flexDirection: "row",
      "& .status": crewStatus(theme),
    },
    "& li.MuiListItem-root": { margin: "0px -15px" },
  },
}));

// Will return true or false for a given position based on whether it's fullfilled in all
// associated pairings or not.
const checkCrewAssignments = (pos: string, countRequired: number, actualCrew: IFPSActualCrew[]): boolean => {
  // find out how many of these positions are fulfilled in actualCrew arra and return true if it exceed required.
  const posFilled = actualCrew.filter((a) => a.pos === pos && !!a.crew);
  return posFilled?.length >= countRequired;
};

/**
 * Context Menu for the Flight Pairing Status Window
 * @param props - Properties
 */
const FlightPairingStatusContextMenu = (props: IFlightPairingStatusContextProps): JSX.Element => {
  const { id, path, setActiveEventModal } = props;
  const theme: Theme = useTheme();
  const dispatch = useDispatch();
  const allMenus = useSelector((state: IRootState) => state.contextMenu.menus);
  const fps: IFlightPairingStatus = useSelector((state: IRootState) => state.flightPairingStatus.selectedFlightStatus);
  const contextMenuHeader: string = useMemo(() => "Flight Pairing Status", []); // Add shortcode

  // determine if context menu is open
  const open = useMemo(() => {
    const contextMenuState = allMenus.get(id);
    return contextMenuState?.isOpen ?? false;
  }, [allMenus, id]);

  const [currentAircraft, setCurrentAircraft] = useState<IAircraft | null>(null);
  useEffect(() => {
    let ignore = false;
    setCurrentAircraft(null);
    if (fps?.aircraftId) {
      void AmplifyAPIWrapper.Get<IAircraft>(`/aircraft/${fps.aircraftId}`).then((response) => {
        if (!ignore && response?._id) setCurrentAircraft(response);
      });
    }
    return () => {
      ignore = true;
    };
  }, [fps?.aircraftId]);

  if (!fps?._id) {
    return null;
  }

  const flightId = fps?._id;
  const {
    _id,
    flightNumber,
    aircraftRegistration,
    desiredCrew,
    actualCrew,
    aircraftConfiguration,
    origin,
    destination,
    actualOut,
    pairingStatuses,
    flightType,
  } = fps;

  const flightChip: IFlightChip = {
    _id: _id,
    flightNumber: flightNumber,
    flightType: flightType,
    origin: origin,
    destination: destination,
    aircraft: currentAircraft ?? undefined,
  };

  // Create array from the pairing map stored in the flight pairing status object.
  const pairings = Object.values(pairingStatuses?.pairings ?? {});

  const editFlight = (): void => {
    dispatch(closeContextMenu(id));
    dispatch(
      openWindow(
        "Flight Editor",
        "BetaEventEditor",
        path,
        {
          eventId: flightId,
          isFlight: true,
          aircraftId: aircraftRegistration,
        },
        "context"
      )
    );
  };

  // Create new pairing from selecte flight.
  const createNewPairing = async (): Promise<void> => {
    // Retrieve the flight data since the aggregate contains a reduced set not compatible with the pairing editor.
    const fullFlight = await AmplifyAPIWrapper.Get<IFullFlight>(`/flights/${flightId}`);
    if (fullFlight?._id) {
      const { path } = props;
      dispatch(
        openWindow(
          "New Pairing",
          "PairingEditorWindow",
          path,
          {
            pairingId: `new_${v1()}`,
            flights: [fullFlight],
          },
          null
        )
      );
    }
  };

  // Assign flight(s) to existing pairing.
  const assignFlightsToPairing = async (): Promise<void> => {
    // Retrieve the flight data since the aggregate contains a reduced set not compatible with the pairing editor.
    const fullFlight = await AmplifyAPIWrapper.Get<IFullFlight>(`/flights/${flightId}`);
    if (fullFlight?._id) {
      dispatch(setSelectedEvents([fullFlight]));
      setActiveEventModal(EventModalTypes.AssignToPairing);
    }
  };

  return (
    <OCContextMenu key={`${id}-contextMenu`} id={`${id}`} menuHeader={contextMenuHeader} isOpen={open} theme={theme}>
      <Grid container direction="column">
        <CardContent className="flight_info">
          <Grid container direction="column" justifyContent={"center"}>
            <Grid item>
              <Grid container direction="row">
                <FlightChip className="flightchip" data={flightChip} />
                <IconButton onClick={editFlight} className="edit_flight_btn" size="large">
                  <EditRounded />
                </IconButton>
              </Grid>
              <Grid>
                <Grid container direction="row" alignItems="center">
                  <Typography className="info aircraft" variant="body2">
                    Aircraft:
                  </Typography>
                  <Typography className="info aircraft" variant="body2" color="textPrimary">
                    {aircraftRegistration ?? "Unassigned"}
                  </Typography>
                </Grid>

                <Grid container direction="row" alignItems="center">
                  <Typography className="info aircraft" variant="body2">
                    Aircraft Config:
                  </Typography>
                  <Typography className="info aircraft" variant="body2" color="textPrimary">
                    {aircraftConfiguration?.configTitle ?? "Unknown"}
                  </Typography>
                </Grid>

                <Grid container direction="row" alignItems="center">
                  <Grid item>
                    <Typography className="info crew" variant="body2">
                      Required Crew:
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Grid container direction="row">
                      {desiredCrew?.length > 0 ? (
                        desiredCrew.map((d, crewindex) => {
                          if (d.count === 0 || d.count === null) {
                            return <Fragment key={crewindex}></Fragment>;
                          }
                          return (
                            <PositionBadge
                              className="crew_badge"
                              key={crewindex}
                              positionTitle={d.positionCode}
                              count={d.count}
                              filled={checkCrewAssignments(d.positionCode, d.count, actualCrew)}
                            />
                          );
                        })
                      ) : (
                        <Typography className="info aircraft" variant="body2" color="textPrimary">
                          Unknown
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Grid>

                <Grid container direction="row" alignItems="center">
                  <Typography className="info departure" variant="body2">
                    Departure:
                  </Typography>
                  <Typography className="info departure" variant="body2" color="textPrimary">
                    {actualOut ? new Date(actualOut).toUTCString() : "Not Set"}
                  </Typography>
                </Grid>

                <Grid container direction="row" alignItems="center">
                  <Grid item>
                    <Typography className="info crew" variant="body2">
                      Flight Status:
                    </Typography>
                  </Grid>
                  <Grid item className="flight_status_container">
                    <div className={`status ${pairingStatuses.status?.className}`}>
                      {pairingStatuses.status?.message}
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
        <Typography className="info pairings" variant="body2" style={{ fontSize: 12, margin: "5px 21px" }}>
          Pairings:
        </Typography>
        {pairings.length > 0 ? (
          pairings.map((item, index) => {
            const { actualCrew } = item; // status
            const pairingId = actualCrew?.length > 0 ? actualCrew[0].pairing : null;
            const shortCode = actualCrew?.length > 0 ? actualCrew[0].prgCode : null;
            // Required positions will only be solid if they are completely filled by assigned crew.
            return (
              <CardContent key={index} className="pairing_info">
                <Grid container direction="column" key={index}>
                  <Grid container direction="row" className="pairing_header" alignItems="center">
                    {pairingId && shortCode && (
                      <ShortCodeChip code={shortCode} itemId={pairingId} type="pairing" edit={true} />
                    )}
                    <Grid item>
                      <Grid container direction="row" alignItems="center">
                        <Typography className="info pairings" variant="body2" style={{ fontSize: 12, margin: 5 }}>
                          Positions:
                        </Typography>
                        {actualCrew &&
                          actualCrew.map((c, crewindex) => {
                            return (
                              <div key={crewindex} style={{ marginLeft: 5 }}>
                                <PositionBadge
                                  className="crew_badge"
                                  positionTitle={c.pos}
                                  filled={!!c.crew} // Add crew name here (ask Devon for endpoint?)
                                />
                              </div>
                            );
                          })}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            );
          })
        ) : (
          <CardContent className="pairing_info">
            <MenuItem onClick={createNewPairing}>Add Flight to New Pairing</MenuItem>
            <MenuItem onClick={assignFlightsToPairing}>Add Flight to Existing Pairing</MenuItem>
          </CardContent>
        )}
      </Grid>
    </OCContextMenu>
  );
};

export default FlightPairingStatusContextMenu;
